import { useState } from 'react'
import { Button, Logo, Tooltip } from '@highlight/ui'
import { ChatShareWrapper } from '@renderer/chat'
import { ShareToSlackDialog, useCanShareToSlack } from '@renderer/features/share-to-slack'
import { Check, Link2, Link2Off, RotateCw } from 'lucide-react'
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'

// The delete circle only exists once a link does, so it animates its own width
// open rather than reserving a gap the rest of the time.
const deleteButtonVariants: Variants = {
  initial: { opacity: 0, scale: 0.8, width: 0, marginRight: 0 },
  animate: { opacity: 1, scale: 1, width: 'auto', marginRight: 4 },
  exit: { opacity: 0, scale: 0.8, width: 0, marginRight: 0 },
}

const deleteButtonTransition: Transition = { duration: 0.15, ease: 'easeOut' }

interface ChatShareControlProps {
  /** The chat to share. Undefined renders nothing, because there is no thread yet. */
  chatId: string | undefined
  /** Tooltips point away from the toolbar the control sits in. */
  tooltipPosition?: 'top' | 'bottom'
  className?: string
  /** Merged into the share/delete analytics events (e.g. which surface). */
  eventProps?: Record<string, unknown>
  /** Names the chat in the Share to Slack dialog. */
  title?: string
}

/**
 * The share affordance for a chat surface: one circle that creates and copies a
 * link, plus a destructive twin that appears once a link exists. Shared by the
 * Chat View header and the action chat drawer so both show the same states and
 * the same copy (HIG-6109).
 */
export function ChatShareControl({
  chatId,
  tooltipPosition = 'bottom',
  className,
  eventProps,
  title,
}: ChatShareControlProps) {
  const canShareToSlack = useCanShareToSlack()

  if (!chatId) {
    return null
  }

  return (
    <ChatShareWrapper chatId={chatId} showTooltip={false} eventProps={eventProps}>
      {({ isDisabled, state, onClick, onDeleteLink, ensureShareLink, tooltip }) => {
        const hasSharedLink = state === 'shared' || state === 'outdated'
        const ShareIcon = state === 'copied' ? Check : state === 'outdated' ? RotateCw : Link2
        return (
          <Tooltip.Provider>
            <div className={twMerge('flex items-center', className)}>
              {canShareToSlack && (
                <ShareChatToSlackButton
                  ensureShareLink={ensureShareLink}
                  disabled={isDisabled}
                  title={title ?? 'Chat'}
                  tooltipPosition={tooltipPosition}
                />
              )}
              <AnimatePresence initial={false}>
                {hasSharedLink && (
                  <motion.div
                    key="delete-share-link"
                    variants={deleteButtonVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={deleteButtonTransition}
                    className="overflow-hidden"
                  >
                    <Tooltip>
                      {/* A wrapper, not the button: it can be disabled, and a disabled button gets no pointer events. */}
                      <Tooltip.Trigger render={<div className="relative flex" />}>
                        <Button
                          variant="ghost"
                          shape="square"
                          onClick={onDeleteLink}
                          disabled={isDisabled}
                          aria-label="Delete share link"
                        >
                          <Link2Off className="text-danger-subtle" />
                        </Button>
                      </Tooltip.Trigger>
                      <Tooltip.Content side={tooltipPosition}>Delete share link</Tooltip.Content>
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>
              <Tooltip>
                <Tooltip.Trigger render={<div className="relative flex" />}>
                  <Button
                    variant="ghost"
                    shape="square"
                    onClick={onClick}
                    loading={state === 'sharing'}
                    disabled={isDisabled}
                    aria-label={tooltip}
                  >
                    <ShareIcon />
                  </Button>
                </Tooltip.Trigger>
                <Tooltip.Content side={tooltipPosition}>{tooltip}</Tooltip.Content>
              </Tooltip>
            </div>
          </Tooltip.Provider>
        )
      }}
    </ChatShareWrapper>
  )
}

interface ShareChatToSlackButtonProps {
  ensureShareLink: () => Promise<string>
  disabled: boolean
  title: string
  tooltipPosition: 'top' | 'bottom'
}

/** A sibling of the link circle rather than a menu on it, so copying a link stays one click. */
function ShareChatToSlackButton({ ensureShareLink, disabled, title, tooltipPosition }: ShareChatToSlackButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  return (
    <>
      <Tooltip>
        <Tooltip.Trigger render={<div className="relative flex" />}>
          <Button
            variant="ghost"
            shape="square"
            onClick={() => setIsDialogOpen(true)}
            disabled={disabled}
            aria-label="Share to Slack"
          >
            <Logo name="slack" size={16} />
          </Button>
        </Tooltip.Trigger>
        <Tooltip.Content side={tooltipPosition}>Share to Slack</Tooltip.Content>
      </Tooltip>
      <ShareToSlackDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        target={{ kind: 'chat', ensureShareLink }}
        title={title}
      />
    </>
  )
}
